export function renderName(
  displayName: string,
  informalName?: string,
  preferredLastName?: string,
  suffixName?: string,
  middleName?: string,
  /** Full legal first name when it can include spaces (e.g. "Dr. Robert"); must match the start of displayName (minus membership suffix). */
  legalFirstName?: string
) {
  // Extract suffix from displayName if present (e.g., ", PGA" for active members)
  const suffixMatch = displayName.match(/,\s*([^,]+)$/);
  const extractedSuffix = suffixMatch ? suffixMatch[1] : "";
  const nameWithoutSuffix = suffixMatch ? displayName.replace(/,\s*[^,]+$/, "").trim() : displayName;

  const trimmedLegal = legalFirstName?.trim();
  let firstSegment: string;
  let nameParts: string[];

  if (trimmedLegal) {
    const afterLegalPrefix = `${trimmedLegal} `;
    if (nameWithoutSuffix === trimmedLegal) {
      firstSegment = trimmedLegal;
      nameParts = [];
    } else if (nameWithoutSuffix.startsWith(afterLegalPrefix)) {
      firstSegment = trimmedLegal;
      const remainder = nameWithoutSuffix.slice(afterLegalPrefix.length);
      nameParts = remainder.trim() ? remainder.trim().split(/\s+/).filter(Boolean) : [];
    } else {
      const [firstName, ...splitParts] = nameWithoutSuffix.split(" ");
      firstSegment = firstName;
      nameParts = splitParts;
    }
  } else {
    const [firstName, ...splitParts] = nameWithoutSuffix.split(" ");
    firstSegment = firstName;
    nameParts = splitParts;
  }

  const middleInitial = middleName?.trim()?.charAt(0)?.toUpperCase();
  const parsedMiddleName = middleName?.trim()?.toLowerCase();
  const [firstRemainingPart, ...remainingParts] = nameParts;
  const cleanedNameParts =
    firstRemainingPart && middleInitial
      ? [firstRemainingPart, ...remainingParts].filter((part, index) => {
          if (index !== 0) return true;
          const normalized = part.toLowerCase().replace(/\./g, "");
          return !(normalized === middleInitial.toLowerCase() || normalized === parsedMiddleName?.replace(/\./g, ""));
        })
      : nameParts;
  const lastName = cleanedNameParts.join(" ");

  let formattedFirstName = firstSegment;
  if (informalName && informalName !== firstSegment) {
    formattedFirstName = `${firstSegment} (${informalName})`;
  }
  if (middleInitial) {
    formattedFirstName = `${formattedFirstName} ${middleInitial}.`;
  }

  let formattedLastName = lastName;
  if (preferredLastName && preferredLastName !== lastName) {
    formattedLastName = `${lastName} (${preferredLastName})`;
  }

  const personalSuffixPart = suffixName ? ` ${suffixName}` : "";
  const membershipSuffixPart = extractedSuffix ? `, ${extractedSuffix}` : "";

  const namePart = [formattedFirstName, formattedLastName].filter(Boolean).join(" ");
  return `${namePart}${personalSuffixPart}${membershipSuffixPart}`.trim();
}

const MEMBERSHIP_SUFFIX_PATTERN = /,\s*([^,]+)$/;

function stripMembershipSuffix(name: string): { core: string; membershipSuffix: string } {
  const match = name.match(MEMBERSHIP_SUFFIX_PATTERN);
  if (!match) {
    return { core: name.trim(), membershipSuffix: "" };
  }

  return {
    core: name.replace(MEMBERSHIP_SUFFIX_PATTERN, "").trim(),
    membershipSuffix: match[1]
  };
}

function withMembershipSuffix(displayCore: string, legalName: string): string {
  const { membershipSuffix } = stripMembershipSuffix(legalName);
  return membershipSuffix ? `${displayCore}, ${membershipSuffix}` : displayCore;
}

function isAbbreviatedNameToken(token: string): boolean {
  return /^[A-Z](?:\.[A-Z])?\.$/.test(token);
}

function isLastNameOnlyPreferred(preferredCore: string, legalCore: string): boolean {
  const preferredTokens = preferredCore.split(/\s+/).filter(Boolean);
  const legalTokens = legalCore.split(/\s+/).filter(Boolean);
  if (preferredTokens.length !== 1 || legalTokens.length < 2) return false;

  const legalLast = legalTokens[legalTokens.length - 1];
  return preferredCore.toLowerCase() === legalLast.toLowerCase();
}

export function isLastNameOnlyPreferredName(legalName: string, preferredName?: string): boolean {
  const legal = legalName?.trim() ?? "";
  const preferred = preferredName?.trim() ?? "";
  if (!legal || !preferred) return false;

  const { core: legalCore } = stripMembershipSuffix(legal);
  const { core: preferredCore } = stripMembershipSuffix(preferred);
  const { core: legalNameCore } = stripPersonalSuffix(legalCore);
  return isLastNameOnlyPreferred(preferredCore, legalNameCore);
}

function shouldUsePreferredNameAsDisplay(preferredCore: string): boolean {
  const [firstToken] = preferredCore.split(/\s+/).filter(Boolean);
  return Boolean(firstToken && isAbbreviatedNameToken(firstToken));
}

const PERSONAL_SUFFIX_PATTERN = /^(Jr\.?|Sr\.?|II|III|IV|V|VI|VII|VIII|IX|X)$/i;

function stripPersonalSuffix(nameCore: string): { core: string; personalSuffix?: string } {
  const tokens = nameCore.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) {
    return { core: nameCore };
  }

  const lastToken = tokens[tokens.length - 1];
  if (!PERSONAL_SUFFIX_PATTERN.test(lastToken)) {
    return { core: nameCore };
  }

  return {
    core: tokens.slice(0, -1).join(" "),
    personalSuffix: lastToken.replace(/\.$/, "")
  };
}

function tokenizeName(name: string): string[] {
  return name.split(/\s+/).filter(Boolean);
}

function countCommonSuffixTokens(legalTokens: string[], preferredTokens: string[]): number {
  const max = Math.min(legalTokens.length, preferredTokens.length);
  let count = 0;

  for (let index = 1; index <= max; index++) {
    const legalToken = legalTokens[legalTokens.length - index];
    const preferredToken = preferredTokens[preferredTokens.length - index];
    if (legalToken.toLowerCase() !== preferredToken.toLowerCase()) {
      break;
    }
    count++;
  }

  return count;
}

function legalNameForRender(legal: string, legalNameCore: string): string {
  const { membershipSuffix } = stripMembershipSuffix(legal);
  return membershipSuffix ? `${legalNameCore}, ${membershipSuffix}` : legalNameCore;
}

/**
 * Formats an Algolia person hit using the same legal + parenthetical preferred pattern as renderName.
 * Index fields: `name` (legal full name) and `preferred_name` (preferred full name).
 */
export function renderNameFromAlgoliaHit(legalName: string, preferredName?: string): string {
  const legal = legalName?.trim() ?? "";
  const preferred = preferredName?.trim() ?? "";

  if (!legal) return preferred || "Unknown Person";
  if (!preferred || preferred.toLowerCase() === legal.toLowerCase()) return legal;

  const { core: legalCore } = stripMembershipSuffix(legal);
  const { core: preferredCore } = stripMembershipSuffix(preferred);

  if (isLastNameOnlyPreferred(preferredCore, legalCore)) return legal;

  if (shouldUsePreferredNameAsDisplay(preferredCore)) {
    return withMembershipSuffix(preferredCore, legal);
  }

  const { core: legalNameCore, personalSuffix } = stripPersonalSuffix(legalCore);
  const legalTokens = tokenizeName(legalNameCore);
  const preferredTokens = tokenizeName(preferredCore);

  if (legalTokens.length < 2) {
    return withMembershipSuffix(preferredCore, legal);
  }

  const displayName = legalNameForRender(legal, legalNameCore);
  const suffixCount = countCommonSuffixTokens(legalTokens, preferredTokens);

  if (suffixCount > 0) {
    const legalFirstPart = legalTokens.slice(0, -suffixCount).join(" ");
    const preferredFirstPart = preferredTokens.slice(0, -suffixCount).join(" ");
    const informalName =
      preferredFirstPart && preferredFirstPart.toLowerCase() !== legalFirstPart.toLowerCase()
        ? preferredFirstPart
        : undefined;

    return renderName(displayName, informalName, undefined, personalSuffix, undefined, legalFirstPart);
  }

  if (preferredTokens.length === 1) {
    const legalLast = legalTokens[legalTokens.length - 1];
    if (preferredCore.toLowerCase() === legalLast.toLowerCase()) {
      return legal;
    }

    const legalFirstPart = legalTokens.slice(0, -1).join(" ");
    return renderName(displayName, preferredCore, undefined, personalSuffix, undefined, legalFirstPart);
  }

  if (legalTokens.length === 2 && preferredTokens.length === 2) {
    const informalName =
      preferredTokens[0].toLowerCase() !== legalTokens[0].toLowerCase() ? preferredTokens[0] : undefined;
    const preferredLastName =
      preferredTokens[1].toLowerCase() !== legalTokens[1].toLowerCase() ? preferredTokens[1] : undefined;

    return renderName(displayName, informalName, preferredLastName, personalSuffix, undefined, legalTokens[0]);
  }

  return withMembershipSuffix(preferredCore, legal);
}
